import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ValidateCoredataUrl } from 'app/helpers/validators';
import { CoredataDocumentRef } from 'app/interfaces/coredataDocumentRef';
import { ExposedCreatePensionApplicationOtherFundsReq } from 'app/interfaces/requests/Exposed/exposedCreatePensionApplicationOtherFundsReq';
import { ExposedListFundPartnerFundsRes } from 'app/interfaces/responses/Exposed/exposedListFundPartnerFundsRes';
import { ExposedListPublicRegistryPersonsRes } from 'app/interfaces/responses/Exposed/exposedListPublicRegistryPersonsRes';
import { ExposedReadCoredataFileSenderRes } from 'app/interfaces/responses/Exposed/exposedReadCoredataFileSenderRes';
import { ExposedSubmitOtherFundsApplicationRes } from 'app/interfaces/responses/Exposed/exposedSubmitOtherFundsApplicationRes';
import { OtherFundsApplicationsService } from 'app/services/api/applications/other-funds-applications.service';
import { PublicRegistrySearchComponent } from 'common/components/public-registry-search/public-registry-search.component';
import { PensionFunds } from 'common/constants/pension-funds.constants';

@Component({
    selector: 'app-pension-retirement-form',
    templateUrl: './pension-retirement-form.component.html',
    styleUrls: ['./pension-retirement-form.component.scss'],
})
export class PensionRetirementFormComponent implements OnInit {
    readonly applicationTypeKey = 'PensionApplication_OF';

    @ViewChild(PublicRegistrySearchComponent) publicRegistrySearch: PublicRegistrySearchComponent;

    @Output() submitted = new EventEmitter<ExposedSubmitOtherFundsApplicationRes>();
    @Output() inputStarted = new EventEmitter<void>();

    form: UntypedFormGroup;

    availableFunds: Array<ExposedListFundPartnerFundsRes> = [];
    fundsLoaded = false;
    lookupInProgress = false;
    submitting = false;
    applicantSelectionError = false;
    signedDocError = false;
    additionalDocError = false;

    constructor(
        private otherFundsApplicationsService: OtherFundsApplicationsService,
        private formBuilder: UntypedFormBuilder
    ) {}

    ngOnInit(): void {
        this.form = this.formBuilder.group({
            applicantSSN: new UntypedFormControl('', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]),
            selectedFunds: new UntypedFormControl([]),
            signedDocUrl: new UntypedFormControl('', [Validators.required, ValidateCoredataUrl()]),
            signedDocName: new UntypedFormControl('', [Validators.required]),
            additionalDocs: this.formBuilder.array([]),
        });
    }

    get additionalDocs(): UntypedFormArray {
        return this.form.get('additionalDocs') as UntypedFormArray;
    }

    addAdditionalDoc(): void {
        this.additionalDocs.push(
            this.formBuilder.group({
                url: new UntypedFormControl('', [Validators.required, ValidateCoredataUrl()]),
                name: new UntypedFormControl('', [Validators.required]),
            })
        );
    }

    removeAdditionalDoc(index: number): void {
        this.additionalDocs.removeAt(index);
    }

    onSignedDocChange(): void {
        this.signedDocError = false;
        this.additionalDocError = false;
    }

    onAdditionalDocChange(): void {
        this.additionalDocError = false;
    }

    private hasDuplicateUrls(): boolean {
        const urls: string[] = [];
        const signedUrl = this.form.get('signedDocUrl')?.value?.trim();
        if (signedUrl) {
            urls.push(signedUrl.toLowerCase());
        }
        for (const doc of this.additionalDocs.controls) {
            const url = doc.get('url')?.value?.trim();
            if (url) {
                urls.push(url.toLowerCase());
            }
        }
        const uniqueUrls = new Set(urls);
        return uniqueUrls.size !== urls.length;
    }

    onApplicantSelected(person: ExposedListPublicRegistryPersonsRes | null): void {
        const applicantSSN = person?.ssn ?? '';

        this.inputStarted.emit();
        this.form.get('applicantSSN')?.setValue(applicantSSN);
        this.applicantSelectionError = false;
        this.availableFunds = [];
        this.fundsLoaded = false;
        this.form.get('selectedFunds')?.setValue([]);

        this.tryToLoadFunds();
    }

    tryToLoadFunds(): void {
        const applicantSSN = this.form.get('applicantSSN')?.value;
        if (!applicantSSN) {
            this.applicantSelectionError = true;
            return;
        }

        this.loadFunds(applicantSSN);
    }

    private loadFunds(applicantSSN: string): void {
        this.otherFundsApplicationsService
            .getFundPartnerFunds(applicantSSN)
            .subscribe((funds) => {
                this.availableFunds = funds ?? [];

                // 1. Filter out LSRSER funds
                this.availableFunds = this.availableFunds.filter((fund) => fund.fund !== PensionFunds.LSRSER);

                // 2. Handle LA-A fund:
                // - If both LA and LA-A are present, filter out LA-A (they're handled together via LA)
                // - If only LA-A is present, treat it as LA since LA-A is always paid out of the LA fund
                const hasLAA = this.availableFunds.some((fund) => fund.fund === PensionFunds.LA_A);
                const hasLA = this.availableFunds.some((fund) => fund.fund === PensionFunds.LA);
                if (hasLAA && hasLA) {
                    this.availableFunds = this.availableFunds.filter((fund) => fund.fund !== PensionFunds.LA_A);
                } else if (hasLAA) {
                    this.availableFunds = this.availableFunds.map((fund) =>
                        fund.fund === PensionFunds.LA_A ? { ...fund, fund: PensionFunds.LA } : fund
                    );
                }

                this.fundsLoaded = true;
                this.form.get('selectedFunds')?.setValue(this.availableFunds.map((fund) => fund.fund));
            })
            .add(() => {
                this.lookupInProgress = false;
            });
    }

    onFundToggle(fundType: string, checked: boolean): void {
        const currentFunds = [...(this.form.get('selectedFunds')?.value ?? [])];
        const fundIndex = currentFunds.indexOf(fundType);
        if (checked && fundIndex === -1) {
            currentFunds.push(fundType);
        }
        if (!checked && fundIndex !== -1) {
            currentFunds.splice(fundIndex, 1);
        }
        this.form.get('selectedFunds')?.setValue(currentFunds);
    }

    isFundSelected(fundType: string): boolean {
        return (this.form.get('selectedFunds')?.value ?? []).includes(fundType);
    }

    submit(): void {
        this.form.markAllAsTouched();

        if (!this.form.get('applicantSSN')?.value) {
            this.applicantSelectionError = true;
        }

        if (this.form.invalid || this.applicantSelectionError) {
            return;
        }

        const selectedFunds = this.form.get('selectedFunds')?.value ?? [];

        const signedDoc: CoredataDocumentRef = {
            url: this.form.get('signedDocUrl')?.value,
            fileName: this.form.get('signedDocName')?.value,
        };
        if (!signedDoc.url || !signedDoc.fileName) {
            this.signedDocError = true;
            return;
        }

        if (this.hasDuplicateUrls()) {
            this.additionalDocError = true;
            return;
        }

        const additionalDocs: CoredataDocumentRef[] = this.additionalDocs.controls.map((group) => ({
            url: group.get('url')?.value,
            fileName: group.get('name')?.value,
        }));

        const payload: ExposedCreatePensionApplicationOtherFundsReq = {
            applicantSSN: this.form.get('applicantSSN')?.value,
            selectedFunds,
            signedApplicationDocument: signedDoc,
            additionalDocuments: additionalDocs.length > 0 ? additionalDocs : undefined,
        };

        this.submitting = true;
        this.otherFundsApplicationsService
            .submitPensionOtherFundsApplication(this.applicationTypeKey, payload)
            .subscribe((result) => {
                this.submitted.emit(result);
                this.resetForm();
            })
            .add(() => {
                this.submitting = false;
            });
    }

    signedDocumentFileNameResolver = (fileInfo: ExposedReadCoredataFileSenderRes): string => {
        const fileName = fileInfo.fileName?.trim();

        const sender = this.getSenderFromFileName(fileName) || '<Heiti lífeyrissjóðs>';
        const applicantSSN = (this.form.get('applicantSSN')?.value ?? '').trim() || '<Kennitala umsækjanda>';

        return `${sender} - E umsókn - ${applicantSSN}`;
    };

    private getSenderFromFileName(fileName?: string): string | undefined {
        if (!fileName || fileName.trim().length === 0) {
            return undefined;
        }

        const index = fileName.indexOf('-');

        if (index <= 0) {
            return fileName.trim();
        }

        return fileName.substring(0, index).trim();
    }

    private resetForm(): void {
        this.form.reset({
            applicantSSN: '',
            selectedFunds: [],
            signedDocUrl: '',
            signedDocName: '',
        });
        this.publicRegistrySearch?.clearSelection();
        this.applicantSelectionError = false;
        while (this.additionalDocs.length) {
            this.additionalDocs.removeAt(0);
        }
        this.availableFunds = [];
        this.fundsLoaded = false;
        this.signedDocError = false;
        this.additionalDocError = false;
    }
}
